import { AbstractEntity } from '@app/shared/abstract/abstract-entity';

export interface EventTemplateItemModel extends AbstractEntity {
  template_id: number;
  name: string;
  description: string;
  days_offset: number;
  time_of_day: string; // Time in HH:mm:ss format
  offset_amount: number;
  offset_unit: 'days' | 'weeks' | 'months';
  offset_direction: 'before' | 'after';
  created_by: number;
  updated_by: number;

  // Relationships
  creator?: { id: number; name: string; email: string };
  updater?: { id: number; name: string; email: string };

  // Computed properties
  formatted_days_offset?: string;
}

export class EventTemplateItemModelUtils {
  static formatDaysOffset(daysOffset: number): string {
    if (daysOffset === 0) {
      return 'Event day';
    } else if (daysOffset === -1) {
      return '1 day before';
    } else if (daysOffset === 1) {
      return '1 day after';
    } else if (daysOffset < 0) {
      return `${Math.abs(daysOffset)} days before`;
    } else {
      return `${daysOffset} days after`;
    }
  }

  static calculateDaysOffset(amount: number, unit: 'days' | 'weeks' | 'months', direction: 'before' | 'after'): number {
    let days = amount;
    
    switch (unit) {
      case 'weeks':
        days = amount * 7;
        break;
      case 'months':
        days = amount * 30;
        break;
    }
    
    return direction === 'before' ? -days : days;
  }

  static formatOffsetDisplay(amount: number, unit: 'days' | 'weeks' | 'months', direction: 'before' | 'after'): string {
    if (amount === 0) {
      return 'Event day';
    }
    
    const unitLabel = amount === 1 ? unit.slice(0, -1) : unit;
    return `${amount} ${unitLabel} ${direction}`;
  }

  static formatTimeOfDay(timeString: string): string {
    if (!timeString) return '';

    // Handle both HH:mm:ss and HH:mm formats
    const timeParts = timeString.split(':');
    const hours = parseInt(timeParts[0], 10);
    const minutes = timeParts[1] || '00';

    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;

    return `${displayHours}:${minutes} ${ampm}`;
  }

  static parseTimeOfDay(timeString: string): string {
    // Convert 12-hour format to 24-hour format for API
    const timeRegex = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i;
    const match = timeString.match(timeRegex);

    if (!match) {
      // Assume it's already in 24-hour format
      return timeString;
    }

    const [, hoursStr, minutes, ampm] = match;
    let hours = parseInt(hoursStr, 10);

    if (ampm.toUpperCase() === 'PM' && hours !== 12) {
      hours += 12;
    } else if (ampm.toUpperCase() === 'AM' && hours === 12) {
      hours = 0;
    }

    return `${hours.toString().padStart(2, '0')}:${minutes}:00`;
  }
}
